import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT } from "../lib/brand";
import { useReveal } from "../hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InstagramIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"
       strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="3" y="3" width="18" height="18" rx="4" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" />
  </svg>
);
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"
       strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M9 12a3 3 0 1 0 3 3V4c.6 2.4 2.4 4 5 4" />
  </svg>
);
const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"
       strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M14 9V6.5A1.5 1.5 0 0 1 15.5 5H17" />
    <path d="M10 10h7" />
    <path d="M14 10v10" />
    <rect x="3" y="3" width="18" height="18" rx="1" />
  </svg>
);

export const Contact = () => {
  const leftRef = useReveal();
  const rightRef = useReveal();

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please email us directly.";
      toast.error(typeof msg === "string" ? msg : "Request failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-40 border-b border-black/100"
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-10 md:gap-16">
        {/* Left — info */}
        <div ref={leftRef} className="col-span-12 md:col-span-5 reveal">
          <div className="label-eyebrow mb-6">Correspondence</div>
          <h2 className="font-display text-[2.8rem] md:text-7xl leading-[0.95] tracking-[-0.015em]">
            Write <span className="italic">to us.</span>
          </h2>

          <p className="font-body text-[15px] leading-[1.85] text-black/75 mt-8 max-w-md">
            For bookings, quotes, or a simple hello — we read every message
            personally and respond within a day.
          </p>

          <div className="mt-12 space-y-8">
            <div>
              <div className="label-micro opacity-60 mb-2">Email</div>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-link"
                className="font-display text-xl md:text-2xl link-underline"
              >
                {CONTACT.email}
              </a>
            </div>
            <div>
              <div className="label-micro opacity-60 mb-2">Phone</div>
              <a
                href={CONTACT.phoneHref}
                data-testid="contact-phone-link"
                className="font-display text-xl md:text-2xl link-underline"
              >
                {CONTACT.phone}
              </a>
            </div>
            <div>
              <div className="label-micro opacity-60 mb-2">Studio</div>
              <div className="font-display text-xl md:text-2xl">
                {CONTACT.location}
              </div>
              <div className="label-micro opacity-60 mt-2">{CONTACT.hours}</div>
            </div>
          </div>

          {/* Social */}
          <div className="mt-14 pt-10 border-t border-black/100">
            <div className="label-eyebrow mb-6">Follow the house</div>
            <div className="flex items-center gap-6">
              <a
                href={CONTACT.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="social-instagram"
                className="group inline-flex items-center gap-3"
              >
                <span className="w-12 h-12 border border-black rounded-full flex items-center justify-center transition-colors group-hover:bg-black group-hover:text-white">
                  <InstagramIcon className="w-5 h-5" />
                </span>
                <span className="label-micro link-underline">Instagram</span>
              </a>
              <a
                href={CONTACT.social.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                data-testid="social-tiktok"
                className="group inline-flex items-center gap-3"
              >
                <span className="w-12 h-12 border border-black rounded-full flex items-center justify-center transition-colors group-hover:bg-black group-hover:text-white">
                  <TikTokIcon className="w-5 h-5" />
                </span>
                <span className="label-micro link-underline">TikTok</span>
              </a>
              <a
                href={CONTACT.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="social-facebook"
                className="group inline-flex items-center gap-3"
              >
                <span className="w-12 h-12 border border-black rounded-full flex items-center justify-center transition-colors group-hover:bg-black group-hover:text-white">
                  <FacebookIcon className="w-5 h-5" />
                </span>
                <span className="label-micro link-underline">Facebook</span>
              </a>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div
          ref={rightRef}
          className="col-span-12 md:col-span-6 md:col-start-7 reveal reveal-delay-2"
        >
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="flex flex-col gap-8 md:gap-10 pt-2"
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              <div>
                <label className="label-micro opacity-60">Full name</label>
                <input
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Your name"
                  className="input-line"
                  data-testid="contact-input-name"
                  required
                />
              </div>
              <div>
                <label className="label-micro opacity-60">Email</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="you@email.com"
                  className="input-line"
                  data-testid="contact-input-email"
                  required
                />
              </div>
            </div>

            <div>
              <label className="label-micro opacity-60">Phone (optional)</label>
              <input
                name="phone"
                type="tel"
                value={form.phone}
                onChange={onChange}
                placeholder="519-000-0000"
                className="input-line"
                data-testid="contact-input-phone"
              />
            </div>

            <div>
              <label className="label-micro opacity-60">Message</label>
              <textarea
                name="message"
                rows={5}
                value={form.message}
                onChange={onChange}
                placeholder="Tell us about your project, dates & what you're hoping to rent…"
                className="input-line"
                data-testid="contact-input-message"
                required
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-6 pt-2">
              <span className="label-micro opacity-60 max-w-xs">
                By submitting, you agree to be contacted about your booking.
              </span>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="btn-pill btn-pill-solid disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Sending…" : sent ? "Sent ✓" : "Send inquiry"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
